import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phoneInput';
import { Icon } from '@/components/common/Icon';
import { showSuccessNotification } from '@/utils/notifications';
import { useAuth } from '@/hooks/useAuth';
import { useUpdateCurrentUserProfileMutation } from '@/hooks/useCurrentUser';
import { isBlankOrValidPhoneNumber } from '@/utils/phoneNumbers';
import { ChangeEmailForm } from './ChangeEmailForm';

export function ContactSection() {
  const { t } = useTranslation('settings');
  const { currentUser, refreshCurrentUser } = useAuth();
  const updateProfileMutation = useUpdateCurrentUserProfileMutation();
  const currentEmail = currentUser === null ? '' : currentUser.email;
  const isEmailVerified = currentUser !== null && currentUser.isEmailVerified === true;
  const pendingEmail =
    currentUser === null || currentUser.pendingEmail === null ? '' : currentUser.pendingEmail;
  const currentPhone = currentUser === null || currentUser.phone === null ? '' : currentUser.phone;
  const [phone, setPhone] = useState(currentPhone);
  const [errorMessage, setErrorMessage] = useState('');
  const [isPhoneValidationVisible, setIsPhoneValidationVisible] = useState(false);

  const isUnchanged = phone.trim() === currentPhone.trim();

  async function submitContact(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    setErrorMessage('');
    if (isUnchanged) {
      return;
    }
    if (isBlankOrValidPhoneNumber(phone) === false) {
      setIsPhoneValidationVisible(true);
      return;
    }
    const nextPhone = phone.trim().length === 0 ? null : phone.trim();
    try {
      await updateProfileMutation.mutateAsync({ phone: nextPhone });
      await refreshCurrentUser();
      showSuccessNotification(t('practitioner.contact.saved'));
    } catch (updateError) {
      const backendMessage = resolveApiErrorMessage(updateError);
      setErrorMessage(
        backendMessage.length > 0 ? backendMessage : t('practitioner.contact.saveError')
      );
    }
  }

  return (
    <Card padding="lg">
      <h2 className="text-lg font-bold text-zooindigo">{t('practitioner.contact.heading')}</h2>

      <div className="mt-6 space-y-1.5">
        <div className="flex items-center gap-2">
          <Label htmlFor="contact-email">{t('practitioner.contact.email')}</Label>
          {isEmailVerified ? (
            <Badge tone="success" size="sm">
              {t('practitioner.contact.emailVerified')}
            </Badge>
          ) : (
            <Badge tone="warning" size="sm">
              {t('practitioner.contact.emailUnverified')}
            </Badge>
          )}
        </div>
        <Input id="contact-email" value={currentEmail} disabled readOnly />
        <p className="text-[11px] text-zoomuted">{t('practitioner.contact.emailChangeNote')}</p>

        {pendingEmail.length > 0 ? (
          <div className="flex items-center gap-2 px-3.5 py-2.5 bg-zoowarningbg border border-zoowarningtext rounded-xl text-xs font-medium text-zoowarningtext">
            <Icon name="Clock" />
            {t('practitioner.contact.emailPending', { email: pendingEmail })}
          </div>
        ) : null}

        <ChangeEmailForm />
      </div>

      <form onSubmit={submitContact} className="mt-4 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="contact-phone">{t('practitioner.contact.phone')}</Label>
          <PhoneInput
            inputId="contact-phone"
            value={phone}
            isValidationVisible={isPhoneValidationVisible}
            onChange={setPhone}
          />
        </div>

        {errorMessage.length > 0 ? (
          <div
            role="alert"
            className="flex items-center gap-2 px-3.5 py-2.5 bg-zooerrorbg border border-zooerrorborder rounded-xl text-xs font-medium text-zooerrortext"
          >
            <Icon name="AlertCircle" />
            {errorMessage}
          </div>
        ) : null}

        <Button type="submit" disabled={isUnchanged} isLoading={updateProfileMutation.isPending}>
          {t('practitioner.contact.save')}
        </Button>
      </form>
    </Card>
  );
}
